import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Truck, ClipboardSignature, Wrench, Tags, Users2,
  Package, FileText, LogOut, Menu, Zap, Settings, FolderCog, ListChecks, ChevronDown, Building2, Layers,
} from "lucide-react";
import logoUrl from "@/assets/powercom-logo.png";
import NotificationBell from "@/components/NotificationBell";

const roleLabels = { admin: "Amministratore", viewer: "Visualizzatore", employee: "Assegnatario" };

function navForRole(role) {
  if (role === "employee") {
    return [
      { to: "/dotazioni", label: "Le mie dotazioni", icon: Package },
      { to: "/documenti", label: "I miei documenti", icon: FileText },
    ];
  }
  const base = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/beni", label: "Beni & Mezzi", icon: Truck },
    { to: "/assegnazioni", label: "Assegnazioni", icon: ClipboardSignature },
    { to: "/manutenzioni", label: "Attività", icon: Wrench },
  ];
  return base;
}

function Brand() {
  return (
    <div className="flex flex-col justify-center px-6 h-[72px] border-b border-border">
      <img src={logoUrl} alt="POWERCOM" data-testid="brand-logo" className="h-7 w-auto self-start" />
      <div className="label-caps text-[0.6rem] text-muted-foreground mt-1.5">Asset Manager</div>
    </div>
  );
}

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-3 rounded-md text-[0.95rem] font-medium transition-colors border-l-[3px] ${
    isActive
      ? "bg-secondary text-primary border-primary"
      : "text-foreground/70 border-transparent hover:bg-secondary/60 hover:text-foreground"
  }`;

function SidebarContent({ user, onLogout, onNavigate }) {
  const items = navForRole(user.role);
  const isAdmin = user.role === "admin";
  const location = useLocation();
  const [openAnag, setOpenAnag] = useState(location.pathname.startsWith("/anagrafiche"));

  return (
    <div className="flex flex-col h-full bg-card overflow-y-auto">
      <Brand />
      <nav className="flex-1 py-4 space-y-1 px-3">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} onClick={onNavigate} data-testid={`nav-${to.replace(/\//g, "")}`} className={linkClass}>
            <Icon className="h-5 w-5 shrink-0" strokeWidth={2} />
            {label}
          </NavLink>
        ))}

        {isAdmin && (
          <div className="pt-2">
            <button
              onClick={() => setOpenAnag((v) => !v)}
              data-testid="nav-anagrafiche-toggle"
              className="w-full flex items-center justify-between px-3 py-3 rounded-md text-[0.95rem] font-medium text-foreground/70 hover:bg-secondary/60 hover:text-foreground border-l-[3px] border-transparent"
            >
              <span className="flex items-center gap-3"><FolderCog className="h-5 w-5" strokeWidth={2} /> Anagrafiche</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${openAnag ? "rotate-180" : ""}`} />
            </button>
            {openAnag && (
              <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1">
                <NavLink to="/anagrafiche/categorie" onClick={onNavigate} data-testid="nav-categorie" className={linkClass}>
                  <Tags className="h-5 w-5 shrink-0" strokeWidth={2} /> Categorie
                </NavLink>
                <NavLink to="/anagrafiche/tipologie" onClick={onNavigate} data-testid="nav-tipologie" className={linkClass}>
                  <Layers className="h-5 w-5 shrink-0" strokeWidth={2} /> Tipologie
                </NavLink>
                <NavLink to="/anagrafiche/attivita" onClick={onNavigate} data-testid="nav-attivita" className={linkClass}>
                  <ListChecks className="h-5 w-5 shrink-0" strokeWidth={2} /> Attività
                </NavLink>
                <NavLink to="/anagrafiche/fornitori" onClick={onNavigate} data-testid="nav-fornitori" className={linkClass}>
                  <Building2 className="h-5 w-5 shrink-0" strokeWidth={2} /> Fornitori
                </NavLink>
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <NavLink to="/dipendenti" onClick={onNavigate} data-testid="nav-dipendenti" className={linkClass}>
            <Users2 className="h-5 w-5 shrink-0" strokeWidth={2} /> Dipendenti
          </NavLink>
        )}

        {isAdmin && (
          <NavLink to="/impostazioni" onClick={onNavigate} data-testid="nav-impostazioni" className={linkClass}>
            <Settings className="h-5 w-5 shrink-0" strokeWidth={2} /> Impostazioni
          </NavLink>
        )}
      </nav>

      <div className="border-t border-border px-6 py-4">
        <div className="text-sm font-semibold truncate">{user.name}</div>
        <div className="text-xs text-muted-foreground truncate">{user.email}</div>
        <div className="label-caps text-[0.6rem] text-primary mt-1">{roleLabels[user.role] || user.role}</div>
      </div>
      <div className="px-6 pb-3">
        <div className="label-caps text-[0.6rem] text-muted-foreground">POWERCOM S.R.L.</div>
        <div className="text-[0.7rem] text-muted-foreground">P.IVA 10280401216</div>
      </div>
      <div className="px-3 pb-4">
        <Button variant="ghost" onClick={onLogout} data-testid="logout-button"
          className="w-full justify-start gap-3 text-foreground/80 hover:text-destructive">
          <LogOut className="h-5 w-5" /> Esci
        </Button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 border-r border-border flex-col z-30">
        <SidebarContent user={user} onLogout={handleLogout} />
      </aside>

      <header className="lg:hidden sticky top-0 z-20 flex items-center justify-between h-16 px-4 bg-card border-b border-border">
        <div className="flex items-center gap-2">
          <img src={logoUrl} alt="POWERCOM" data-testid="brand-logo-mobile" className="h-6 w-auto" />
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-[280px]">
              <SidebarContent user={user} onLogout={handleLogout} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="lg:pl-72 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
